/**
 * Time handling for the working week.
 *
 * Everything in the scoring model is expressed in Israel local time, because that is
 * what opening hours, meal times and Shabbat are all defined against. The app runs on a
 * phone that is almost certainly already in that timezone, but relying on that
 * assumption would break the moment the pipeline runs on a laptop set to UTC — so the
 * conversion is explicit.
 */

import { TLV_TIMEZONE } from './geo.js';

/** A moment in the working week, in Israel local time. */
export interface WeekMoment {
  /** 0 = Sunday, matching JS Date.getDay() and the Israeli week. */
  day: number;
  /** Minutes past local midnight, 0..1439. */
  minutesOfDay: number;
}

const partsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: TLV_TIMEZONE,
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/** Convert an absolute instant to a day/time in Israel local time. */
export function toWeekMoment(date: Date): WeekMoment {
  const parts = partsFormatter.formatToParts(date);
  const lookup = (type: string) => parts.find((p) => p.type === type)?.value ?? '';

  const day = WEEKDAY_INDEX[lookup('weekday')] ?? 0;
  const hour = Number(lookup('hour'));
  const minute = Number(lookup('minute'));

  // Intl renders midnight as "24" in some ICU versions under hour12:false.
  const normalisedHour = hour === 24 ? 0 : hour;

  return { day, minutesOfDay: normalisedHour * 60 + minute };
}

/** Human label for a moment, e.g. "Fri 20:30". Used in the time slider and tests. */
export function formatMoment(moment: WeekMoment): string {
  const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const h = Math.floor(moment.minutesOfDay / 60);
  const m = moment.minutesOfDay % 60;
  return `${names[moment.day]} ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Hebrew day names, for the RTL UI. */
export const HEBREW_DAY_NAMES = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

/**
 * Is this moment inside the Shabbat window?
 *
 * Deliberately approximate — real candle-lighting times come from Hebcal at runtime
 * (see app/src/modifiers). This fallback exists so the pipeline and the offline path
 * still behave sensibly: Friday from 17:00 until Saturday 20:00 covers the year's
 * range well enough for a demand multiplier, where being 40 minutes off changes
 * nothing that matters.
 */
export function isApproximateShabbat(moment: WeekMoment): boolean {
  if (moment.day === 5) return moment.minutesOfDay >= 17 * 60;
  if (moment.day === 6) return moment.minutesOfDay < 20 * 60;
  return false;
}
